import {
  CSRF_HEADER,
  type WikiPageView,
  type WikiPassageAnchor,
  type WikiPassageHistory,
  type WikiReviewSince,
  type WikiReviewSinceItem,
} from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleDot, FileClock, GitCommitVertical, History, X } from "lucide-react";
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { formatDateTime } from "../i18n/format.ts";
import { changeSetPath, Link, wikiPath } from "../router.tsx";
import { useSession } from "../session.tsx";
import { MarkedPage, type ReadingView } from "./MarkedPage.tsx";
import { useWikiVersionCompare, wikiKeys } from "./queries.ts";
import { CommentView } from "./ThreadPanel.tsx";

// wiki-collaboration-ux S5 in the browser: the history of a passage in the companion pane (D17),
// "What changed since my last review" in the review (D18) and "What changed since you last read
// it" on a published page (D18). Everything shown comes from the server's filtered answers: a
// version, set, item or thread the viewer may not read never reaches these views, and a name
// they may not see reads "a member".

// ── History of a passage (D17) ─────────────────────────────────────────────────────────────

/** The passage's history as the viewer may read it. */
export function usePassageHistory(
  projectId: string,
  pageId: string,
  version: number,
  anchor: WikiPassageAnchor | null,
) {
  const { csrfToken } = useSession();
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "passage-history", pageId, version, anchor],
    enabled: !!anchor,
    retry: false,
    staleTime: 30_000,
    queryFn: async (): Promise<WikiPassageHistory> =>
      ok(
        await api.api.projects[":projectId"].wiki.pages[":pageId"]["passage-history"].$post(
          { param: { projectId, pageId }, json: { version, anchor: anchor as WikiPassageAnchor } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
  });
}

/** The pane's body for a passage's history: its origin, its set and that set's threads on it. */
export function PassageHistoryView(props: {
  projectId: string;
  pageId: string;
  version: number;
  anchor: WikiPassageAnchor;
  section: string | null;
  pageTitle: string;
  titleRef: RefObject<HTMLHeadingElement | null>;
  onJump: (() => void) | null;
}) {
  const { t } = useTranslation();
  const history = usePassageHistory(props.projectId, props.pageId, props.version, props.anchor);
  const member = t("wiki.history.passage.member");
  const where = props.section
    ? t("wiki.history.passage.where", { section: props.section, page: props.pageTitle })
    : props.pageTitle;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <h3
          ref={props.titleRef}
          tabIndex={-1}
          className="flex items-center gap-1.5 text-[13px] font-semibold text-fg focus-visible:outline-none"
        >
          <History aria-hidden="true" className="size-4 text-fg-muted" strokeWidth={1.75} />
          {t("wiki.history.passage.title")}
        </h3>
        <blockquote className="line-clamp-4 border-l-2 border-line-strong pl-2.5 text-[13px] text-fg-secondary">
          {props.anchor.quote}
        </blockquote>
        <p className="flex flex-wrap items-center gap-x-2 text-xs text-fg-muted">
          <span>{where}</span>
          {props.onJump ? (
            <>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                onClick={props.onJump}
                className="text-accent-text underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none"
              >
                {t("wiki.review.pane.jump")}
              </button>
            </>
          ) : null}
        </p>
      </div>
      {history.isError ? (
        <InlineError>{history.error.message}</InlineError>
      ) : !history.data ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : !history.data.origin ? (
        <p className="text-[13px] text-fg-secondary">{t("wiki.history.passage.none")}</p>
      ) : (
        <ol className="flex flex-col gap-4" aria-label={t("wiki.history.passage.steps")}>
          <li className="flex gap-2.5">
            <GitCommitVertical
              aria-hidden="true"
              className="mt-0.5 size-4 shrink-0 text-fg-muted"
              strokeWidth={1.75}
            />
            <div className="flex min-w-0 flex-col gap-1">
              <p className="text-[13px] text-fg">
                {t("wiki.history.passage.introduced")}{" "}
                <Link
                  href={wikiPath(props.projectId, props.pageId, history.data.origin.version)}
                  className="font-medium text-accent-text underline-offset-4 hover:underline"
                >
                  {t("wiki.version", { version: history.data.origin.version })}
                </Link>
              </p>
              <p className="text-xs text-fg-muted">
                <time dateTime={history.data.origin.publishedAt}>
                  {formatDateTime(history.data.origin.publishedAt)}
                </time>
                {history.data.set
                  ? null
                  : ` · ${t("wiki.history.passage.writtenBy", {
                      name: history.data.origin.authorName ?? member,
                    })}`}
              </p>
            </div>
          </li>
          {history.data.set ? (
            <li className="flex gap-2.5">
              <FileClock
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              <div className="flex min-w-0 flex-col gap-1">
                <Link
                  href={changeSetPath(props.projectId, history.data.set.id)}
                  data-esi-content="wiki"
                  className="text-[13px] font-medium break-words text-fg underline-offset-4 hover:underline"
                >
                  {history.data.set.title}
                </Link>
                <p className="text-xs text-fg-secondary">
                  {[
                    t("wiki.history.passage.proposedBy", {
                      name: history.data.set.createdByName ?? member,
                    }),
                    ...(history.data.set.approvedByName
                      ? [
                          t("wiki.history.passage.approvedBy", {
                            name: history.data.set.approvedByName,
                          }),
                        ]
                      : []),
                    t("wiki.history.passage.publishedBy", {
                      name: history.data.set.publishedByName ?? member,
                    }),
                  ].join(" · ")}
                </p>
              </div>
            </li>
          ) : null}
          {history.data.set ? (
            <li className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-fg-secondary">
                {t("wiki.history.passage.discussion", { count: history.data.threads.length })}
              </p>
              {history.data.threads.length ? (
                <ul className="flex flex-col divide-y divide-line rounded-md border border-line bg-surface-raised">
                  {history.data.threads.map((th) => (
                    <li key={th.id} className="flex flex-col gap-2 px-3 py-2.5">
                      <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-fg-muted">
                        {th.status === "resolved" ? (
                          <CheckCircle2
                            aria-hidden="true"
                            className="size-3.5"
                            strokeWidth={1.75}
                          />
                        ) : (
                          <CircleDot aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
                        )}
                        <span className="font-medium text-fg-secondary">
                          {t(`wiki.threads.kind.${th.kind}`)}
                        </span>
                        {th.status === "resolved" ? (
                          <span>
                            · {t("wiki.threads.resolvedBy", { name: th.resolvedByName ?? member })}
                          </span>
                        ) : (
                          <span>· {t("wiki.history.passage.open")}</span>
                        )}
                      </p>
                      {th.resolutionNote ? (
                        <p
                          data-esi-content="wiki"
                          className="rounded-[5px] bg-surface-sunken px-2 py-1 text-[13px] text-fg"
                        >
                          {th.resolutionNote}
                        </p>
                      ) : null}
                      {th.quote && th.quote !== props.anchor.quote ? (
                        <p className="text-xs text-fg-muted">
                          {t("wiki.history.passage.onText")}{" "}
                          <q data-esi-content="wiki" className="text-fg-secondary">
                            {th.quote}
                          </q>
                        </p>
                      ) : null}
                      <ul className="flex flex-col gap-2" data-esi-content="wiki">
                        {th.comments.map((c) => (
                          <CommentView
                            key={c.id}
                            comment={{ ...c, mayDelete: false }}
                            busy={false}
                            onDelete={() => {}}
                          />
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-fg-muted">{t("wiki.history.passage.noThreads")}</p>
              )}
            </li>
          ) : null}
        </ol>
      )}
    </div>
  );
}

// ── Since my last review (D18) ─────────────────────────────────────────────────────────────

/** The viewer's own marks on a set; empty items when they decided nothing on it. */
export function useReviewSince(projectId: string, setId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...wikiKeys.all(projectId), "since-review", setId],
    enabled,
    retry: false,
    queryFn: async (): Promise<WikiReviewSince> =>
      ok(
        await api.api.projects[":projectId"].wiki["change-sets"][":id"]["since-review"].$get({
          param: { projectId, id: setId },
        }),
      ),
  });
}

/** One line above the page: how the item stands against the viewer's last review of it. */
export function SinceReviewLine(props: { item: WikiReviewSinceItem | undefined }) {
  const { t } = useTranslation();
  const { item } = props;
  if (!item) {
    return null;
  }
  return (
    <p
      data-since-review={item.state}
      className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-secondary"
    >
      <History aria-hidden="true" className="size-3.5 text-fg-muted" strokeWidth={1.75} />
      <span className="font-medium text-fg">{t(`wiki.history.review.${item.state}`)}</span>
      {item.markedAt && item.decision ? (
        <span className="text-fg-muted">
          ·{" "}
          {t(`wiki.history.review.decision.${item.decision}`, {
            at: formatDateTime(item.markedAt),
          })}
        </span>
      ) : null}
    </p>
  );
}

// ── Since I last read it (D18) ─────────────────────────────────────────────────────────────

/**
 * Records the read of the current version once per page and version, and keeps the version to
 * compare with for this visit (the server answers it once: the next open offers nothing).
 */
export function usePageReadSince(projectId: string, page: WikiPageView) {
  const { csrfToken } = useSession();
  const current = page.shownVersion === page.version;
  const key = `${page.id}:${page.version}`;
  const [state, setState] = useState<{ key: string; since: number | null; hidden: boolean }>({
    key: "",
    since: null,
    hidden: false,
  });
  const sent = useRef<string | null>(null);
  // The page shown now: an answer for another page or version is dropped. Not an effect's
  // cleanup flag: a re-run (a new token, a refetched page) must not drop the one request sent.
  const shownKey = useRef(key);
  shownKey.current = key;
  useEffect(() => {
    if (!current || sent.current === key) {
      return;
    }
    sent.current = key;
    void (async () => {
      try {
        const res = await api.api.projects[":projectId"].wiki.pages[":pageId"].read.$post(
          { param: { projectId, pageId: page.id }, json: { version: page.version } },
          { headers: { [CSRF_HEADER]: csrfToken } },
        );
        // An N-1 API (no route) or a refusal is no line: a reading aid never fails the page.
        if (!res.ok) {
          return;
        }
        const { since } = (await res.json()) as { since: number | null };
        if (shownKey.current === key) {
          setState({ key, since, hidden: false });
        }
      } catch {
        // No line.
      }
    })();
  }, [current, key, projectId, page.id, page.version, csrfToken]);
  const since = current && state.key === key && !state.hidden ? state.since : null;
  return { since, dismiss: () => setState((s) => ({ ...s, hidden: true })) };
}

/** "What changed since you last read it · Show", dismissible; Show marks the page. */
export function ReadSinceLine(props: {
  since: number;
  showing: boolean;
  onShow: (on: boolean) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-read-since=""
      className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-line bg-surface-raised py-1.5 pr-1.5 pl-3 text-[13px] text-fg-secondary"
    >
      <History aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
      <span className="min-w-0 flex-1">
        {t("wiki.history.read.line", { version: props.since })}
      </span>
      <Button
        size="xs"
        variant="outline"
        aria-pressed={props.showing}
        onClick={() => props.onShow(!props.showing)}
      >
        {props.showing ? t("wiki.history.read.hide") : t("wiki.history.read.show")}
      </Button>
      <Button
        size="icon-xs"
        variant="ghost"
        aria-label={t("wiki.history.read.dismiss")}
        onClick={props.onDismiss}
      >
        <X aria-hidden="true" />
      </Button>
    </div>
  );
}

/** The page marked against the version last read (both as the viewer may open them). */
export function ReadSinceMarks(props: {
  projectId: string;
  page: WikiPageView;
  since: number;
  resolve: (title: string) => string | null;
  fallback: ReactNode;
}) {
  const { t } = useTranslation();
  const compare = useWikiVersionCompare(
    props.projectId,
    props.page.id,
    props.since,
    props.page.version,
  );
  const [view, setView] = useState<ReadingView>("marked");
  if (compare.isError) {
    return (
      <>
        <InlineError>{t("wiki.history.read.unavailable")}</InlineError>
        {props.fallback}
      </>
    );
  }
  if (!compare.data) {
    return <Skeleton className="h-40 w-full" />;
  }
  return (
    <MarkedPage
      base={compare.data.from.markdown}
      proposed={compare.data.to.markdown}
      baseVersion={props.since}
      title={props.page.title}
      view={view}
      onView={setView}
      // The page's own heading IDs: its contents navigation keeps working on the marks.
      headingPrefix="wiki-section"
      resolve={props.resolve}
    />
  );
}
